import { useApolloClient } from "@apollo/client";
import {
  type FetchResult,
  type InboxConversationsConversationFragment,
  InboxConversationsConversationFragmentDoc,
  InboxConversationsDocument,
  type InboxConversationsQuery,
  type MutationResult,
  type RemoveInboxConversationLabelMutation,
  useRemoveInboxConversationLabelMutation,
} from "@qw/graphql";
import { KnownInboxLabel } from "@qw/qw-common";
import { useCallback } from "react";
import type { MaybePartialInboxConversation } from "@/types";

import { useInboxFilter } from "./inbox-filter-provider";
import { useInboxQueryStates } from "./inbox-query-states-provider";

export type UseRemoveLabelFromConversationReturn = [
  (
    updatedConversation: MaybePartialInboxConversation,
    label: string
  ) => Promise<FetchResult<RemoveInboxConversationLabelMutation> | undefined>,
  MutationResult<RemoveInboxConversationLabelMutation>,
];

export const useRemoveLabelFromConversation = (): UseRemoveLabelFromConversationReturn => {
  const { conversationId, setConversationId } = useInboxQueryStates();
  const apolloClient = useApolloClient();
  const [removeLabelMutation, result] = useRemoveInboxConversationLabelMutation();
  const { filters: inboxFilter } = useInboxFilter();

  const removeLabel = useCallback(
    async (updatedConversation: MaybePartialInboxConversation, label: string) => {
      const refetchQueries: string[] = [];
      const cacheId = apolloClient.cache.identify({ __typename: "Conversation", id: updatedConversation.id });
      const cachedConversation = cacheId
        ? apolloClient.cache.readFragment<InboxConversationsConversationFragment>({
            id: cacheId,
            fragment: InboxConversationsConversationFragmentDoc,
          })
        : null;
      const attachmentPreviews =
        "attachmentPreviews" in updatedConversation
          ? updatedConversation.attachmentPreviews
          : (cachedConversation?.attachmentPreviews ?? []);
      const draftCount =
        "draftCount" in updatedConversation ? updatedConversation.draftCount : (cachedConversation?.draftCount ?? 0);
      const messageCount =
        "messageCount" in updatedConversation
          ? updatedConversation.messageCount
          : (cachedConversation?.messageCount ?? 0);
      const isRemovingUnreadLabel = label === KnownInboxLabel.UNREAD;
      const wasUnread = updatedConversation.labels?.includes(KnownInboxLabel.UNREAD) ?? false;

      if (isRemovingUnreadLabel || wasUnread) {
        refetchQueries.push("InboxLabelUnreadCounts");
      }

      if (!(isRemovingUnreadLabel && inboxFilter.labels?.includes(KnownInboxLabel.UNREAD))) {
        // IMPORTANT: Don't refetch the query for the `UNREAD` label if it's in the filters.
        refetchQueries.push("InboxConversations");
      }

      return removeLabelMutation({
        refetchQueries,
        variables: { input: { conversationId: updatedConversation.id, label } },
        update(cache, { data }) {
          const conversation = data?.removeInboxConversationLabel;

          if (!conversation) {
            return;
          }

          // IMPORTANT: Don't update cache for the UNREAD label if it's in the filters
          if (label === KnownInboxLabel.UNREAD && inboxFilter.labels?.includes(KnownInboxLabel.UNREAD)) {
            return;
          }

          // Read the existing data from the cache
          const existingData: InboxConversationsQuery | null = cache.readQuery({
            query: InboxConversationsDocument,
            // NOTE: We need to pass the filters used by the current conversations query to access the cache.
            variables: { input: inboxFilter },
          });

          const existingConversations = existingData?.inboxConversations.conversations;

          // If the label is in the filters, we need to update the cache
          if (existingConversations && inboxFilter.labels?.includes(label as KnownInboxLabel)) {
            const itemIndex = existingConversations.findIndex((item) => item.id === conversation.id);

            // Filter out the removed item from the list
            const updatedItems = existingConversations.filter((item) => item.id !== conversation.id);

            // If the active conversation is the one we're updating, navigate to the next (or previous) conversation.
            if (conversationId === updatedConversation.id) {
              const nextItem = existingConversations[itemIndex + 1] || existingConversations[itemIndex - 1];

              // TODO: This needs to update the query params not the path
              setTimeout(() => setConversationId(nextItem ? nextItem.id : null, { history: "replace" }), 10);
            }

            // Write the updated data back to the cache
            cache.writeQuery({
              query: InboxConversationsDocument,
              variables: { input: inboxFilter },
              data: {
                inboxConversations: {
                  ...existingData.inboxConversations,
                  conversations: updatedItems,
                },
              },
            });
          }
        },
        optimisticResponse: {
          removeInboxConversationLabel: {
            ...(cachedConversation ?? {}),
            ...updatedConversation,
            attachmentPreviews,
            draftCount,
            messageCount,
            labels: (updatedConversation.labels ?? []).filter((l) => l !== label),
          },
        },
      });
    },
    [removeLabelMutation, inboxFilter, conversationId, setConversationId, apolloClient]
  );

  return [removeLabel, result];
};
